import type { MeterDescriptor } from '@/types';
import { cn } from '@/lib/cn';
import { Cpu } from 'lucide-react';

interface MeterPickerProps {
  meters: MeterDescriptor[];
  value: string | 'all';
  onChange: (id: string | 'all') => void;
}

export function MeterPicker({ meters, value, onChange }: MeterPickerProps) {
  return (
    <div
      className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-4 px-4 py-1 snap-x snap-mandatory"
      role="group"
      aria-label="Select meter"
    >
      <Chip active={value === 'all'} onClick={() => onChange('all')}>
        All meters
      </Chip>
      {meters.map((m, idx) => (
        <Chip
          key={m.deviceId}
          active={value === m.deviceId}
          onClick={() => onChange(m.deviceId)}
          icon={<Cpu className="w-3 h-3" />}
        >
          M{idx + 1} · {m.label}
        </Chip>
      ))}
    </div>
  );
}

function Chip({
  active, onClick, children, icon,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'snap-start shrink-0 inline-flex items-center gap-1.5',
        'min-h-[36px] px-3 rounded-full',
        'text-[11px] font-semibold transition-all duration-150 border',
        active
          ? 'bg-ink-900 text-white border-ink-900 shadow-card'
          : 'bg-white text-ink-600 border-ink-200 active:bg-ink-50 active:scale-[0.97]',
      )}
    >
      {icon}
      {children}
    </button>
  );
}
